import * as yup from "yup";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Select } from "@normasteaching/norma-ui";
import { Col } from "../../UI/Layout/Col.tsx";
import { Row } from "../../UI/Layout/Row.tsx";
import { TextInput } from "../../UI/TextInput.tsx";
import { InputWithLabel } from "../../UI/InputWithLabel.tsx";
import { PrimaryButton } from "../../UI/PrimaryButton.tsx";
import { useWithLoading } from "../../UI/Loading/useWithLoading.ts";

export type OnboardingLivePdfFormInputs = {
  userGroupId: string;
  pdfUrl: string;
};

type Props = {
  userGroups: { id: string; name: string; slug: string }[];
  defaultValues?: Partial<OnboardingLivePdfFormInputs>;
  onSave: (data: OnboardingLivePdfFormInputs) => Promise<void>;
  disabledMap?: {
    [key in keyof OnboardingLivePdfFormInputs]?: boolean;
  };
};

const schema = yup
  .object({
    userGroupId: yup.string().required("Seleziona un user group"),
    pdfUrl: yup
      .string()
      .trim()
      .url("Specifica un URL valido")
      .required("Specifica un URL per il PDF"),
  })
  .required();

export function OnboardingLivePdfForm({
  userGroups,
  defaultValues,
  onSave,
  disabledMap,
}: Props) {
  const userGroupOptions = userGroups.map((ug) => ({
    id: ug.id,
    name: `${ug.name} (${ug.slug})`,
  }));
  const {
    handleSubmit,
    formState: { errors },
    control,
  } = useForm<OnboardingLivePdfFormInputs>({
    resolver: yupResolver(schema),
    defaultValues,
  });
  const { withLoading, loading } = useWithLoading();
  const onSubmit: SubmitHandler<OnboardingLivePdfFormInputs> = async (data) => {
    await withLoading(async () => onSave(data));
  };

  return (
    <form>
      <Col>
        <Row>
          <InputWithLabel label="User group">
            <Controller
              control={control}
              name={"userGroupId"}
              render={({ field }) => (
                <Select<string>
                  disabled={disabledMap?.userGroupId}
                  errorMessage={errors.userGroupId?.message}
                  onChange={field.onChange}
                  value={field.value}
                  options={userGroupOptions}
                />
              )}
            />
          </InputWithLabel>
        </Row>
        <Row>
          <InputWithLabel label="PDF url">
            <Controller
              control={control}
              name={"pdfUrl"}
              render={({ field }) => (
                <TextInput errorMessage={errors.pdfUrl?.message} {...field} />
              )}
            />
          </InputWithLabel>
        </Row>
        <Row>
          <div className={"flex w-full justify-end"}>
            <PrimaryButton
              loading={loading}
              onClick={handleSubmit(onSubmit)}
              label={"Save"}
            />
          </div>
        </Row>
      </Col>
    </form>
  );
}
